import type { Place } from "./types";

const EARTH_RADIUS_KM = 6371;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function coordinate(place: Pick<Place, "lat" | "lng">): string {
  return `${place.lat},${place.lng}`;
}

export function haversineDistanceKm(a: Place, b: Place): number {
  const deltaLat = toRadians(b.lat - a.lat);
  const deltaLng = toRadians(b.lng - a.lng);
  const latA = toRadians(a.lat);
  const latB = toRadians(b.lat);
  const h =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(latA) * Math.cos(latB) * Math.sin(deltaLng / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function formatDistanceKm(distanceKm: number): string {
  return `${distanceKm.toFixed(1)} km`;
}

export function optimizeRouteOrder(base: Place, stops: Place[]): Place[] {
  const remaining = [...stops];
  const ordered: Place[] = [];
  let current = base;

  while (remaining.length > 0) {
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    remaining.forEach((place, index) => {
      const distance = haversineDistanceKm(current, place);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    const [nearest] = remaining.splice(nearestIndex, 1);
    if (nearest) {
      ordered.push(nearest);
      current = nearest;
    }
  }

  return improveRouteWithTwoOpt(base, ordered);
}

export function totalRouteDistanceKm(places: Place[]): number {
  return places.reduce((total, place, index) => {
    const previous = places[index - 1];
    return previous ? total + haversineDistanceKm(previous, place) : total;
  }, 0);
}

export function buildGoogleMapsDirectionsUrl(places: Place[]): string {
  if (places.length === 0) {
    return "https://www.google.com/maps";
  }

  if (places.length === 1) {
    const onlyPlace = places[0];
    return onlyPlace
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinate(onlyPlace))}`
      : "https://www.google.com/maps";
  }

  const origin = places[0];
  const destination = places[places.length - 1];
  if (!origin || !destination) {
    return "https://www.google.com/maps";
  }

  const waypoints = places.slice(1, -1);
  const params = new URLSearchParams({
    api: "1",
    origin: coordinate(origin),
    destination: coordinate(destination),
    travelmode: "walking"
  });

  if (waypoints.length > 0) {
    params.set("waypoints", waypoints.map(coordinate).join("|"));
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function improveRouteWithTwoOpt(base: Place, stops: Place[]): Place[] {
  if (stops.length < 4) {
    return stops;
  }

  let best = [...stops];
  let improved = true;

  while (improved) {
    improved = false;

    for (let i = 0; i < best.length - 2; i += 1) {
      for (let k = i + 1; k < best.length - 1; k += 1) {
        const candidate = [
          ...best.slice(0, i),
          ...best.slice(i, k + 1).reverse(),
          ...best.slice(k + 1)
        ];
        const currentDistance = totalRouteDistanceKm([base, ...best]);
        const candidateDistance = totalRouteDistanceKm([base, ...candidate]);

        if (candidateDistance < currentDistance) {
          best = candidate;
          improved = true;
        }
      }
    }
  }

  return best;
}
